import { api } from '@/lib/api/client'
import { ApiError, isMaintenance } from '@/lib/api/errors'
import { initialLang, type Lang } from '@/lib/i18n'
import { tenantOf } from './model'
import type { AddressBoot, AddressProfile } from './types'

/*
 * P12 — the boot: `GET /api/address` is asked once, through the API client,
 * before any route tree is created (main.tsx), so the tree, the redirects,
 * the favicon and the first language come from the server's answer rather
 * than from a guess at the host name. It waits four seconds, then — the
 * platform busy rather than gone — once more for twelve; a host the API
 * still cannot be asked about falls back to the host mirror in
 * lib/workspace.ts (model.ts's `treeFor`).
 */

export const ADDRESS_PATH = '/api/address'

const BOOT_TIMEOUT_MS = 4000

/** The second, longer chance (staff roles audit 2026-10-11, G2): one slow answer must not lock every member out. */
const RETRY_TIMEOUT_MS = 12000

let booted: AddressBoot | null = null

/** Asks the API what this address is: once, and once more with longer to answer if it did not answer at all. */
export async function bootAddress(timeoutMs = BOOT_TIMEOUT_MS, retryMs = RETRY_TIMEOUT_MS): Promise<AddressBoot> {
  let boot = await ask(timeoutMs)
  if (boot.state === 'offline' && !boot.maintenance && retryMs > 0) boot = await ask(retryMs)
  booted = boot
  return booted
}

/** What the boot learnt, once it has run (null before). */
export const bootedAddress = (): AddressBoot | null => booted

async function ask(timeoutMs: number): Promise<AddressBoot> {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    const { data } = await api.get<{ data: unknown }>(ADDRESS_PATH, { signal: controller.signal })
    return isProfile(data) ? { state: 'known', address: data, lang: initialLang } : { state: 'offline', maintenance: false }
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return { state: 'none' }
    return { state: 'offline', maintenance: isMaintenance(error) }
  } finally {
    window.clearTimeout(timer)
  }
}

function isProfile(value: unknown): value is AddressProfile {
  if (typeof value !== 'object' || value === null || !('kind' in value) || !('redirect' in value)) return false
  const { kind, redirect } = value as { kind: unknown; redirect: unknown }
  if (typeof redirect !== 'object' || redirect === null) return false
  if (kind === 'platform') return true

  return kind === 'tenant' && 'company' in value && 'canonical' in value && 'serves' in value
}

/**
 * The language a company's workspace and sign-in open in when this browser
 * holds no choice of its own: the company's default (T36's, via the
 * address). A member's own choice still wins — lib/i18n's `adoptDefaultLang`
 * checks for it.
 */
export function companyLanguage(boot: AddressBoot): Lang | null {
  const chosen = tenantOf(boot)?.locales?.workspace_default
  return chosen === 'ar' || chosen === 'en' ? chosen : null
}
